import { useState, type FormEvent } from "react";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { enmascararDocumento, esCorreoDeDocumento, TIPOS_DOCUMENTO } from "@porteria/core";
import { useSesion } from "../sesion";
import { AvisoInstalar, AvisoNotificaciones, FotoPerfil } from "../componentes";
import { Boton, Campo, Dato, Pantalla, Tarjeta, useAccion, useAvisos } from "../ui";
import { FormularioPerfil } from "./Registro";

export default function Perfil() {
  const { perfil, cuenta, membresias, salir } = useSesion();
  const { ejecutar, ocupado } = useAccion();
  const [editando, setEditando] = useState(false);
  const esDomiciliario = (membresias ?? []).some((m) => m.roles?.DOMICILIARIO);
  if (!perfil) return null;
  return (
    <Pantalla titulo="Mi perfil" volver={true}>
      <Tarjeta>
        {editando ? (
          <FormularioPerfil perfil={perfil} textoBoton="Guardar" alGuardar={() => setEditando(false)} />
        ) : (
          <>
            <div className="datos">
              <Dato etiqueta="Nombre">{perfil.nombre}</Dato>
              <Dato etiqueta="Celular">{perfil.celular ?? "—"}</Dato>
              <Dato etiqueta="Correo">{esCorreoDeDocumento(cuenta?.email) ? "Entras con tu documento" : (cuenta?.email ?? "—")}</Dato>
              <Dato etiqueta={TIPOS_DOCUMENTO.find((t) => t.valor === perfil.tipoDocumento)?.etiqueta ?? "Documento"}>
                {enmascararDocumento(perfil.documentoUltimos4)}
              </Dato>
            </div>
            <Boton variante="secundario" onClick={() => setEditando(true)}>
              Editar mis datos
            </Boton>
          </>
        )}
      </Tarjeta>
      <Tarjeta className={esDomiciliario && !perfil.fotoUrl ? "alerta" : undefined}>
        <h2>Mi foto</h2>
        <FotoPerfil
          texto={
            esDomiciliario
              ? "Va en tu carné. El vigilante la compara con tu cara antes de entregarte un paquete: que se te vea bien el rostro."
              : "Opcional para residentes. Si quieres llevar paquetes, la necesitas."
          }
        />
      </Tarjeta>
      {cuenta?.providerData.some((p) => p.providerId === "password") && <CambiarClave />}
      <AvisoNotificaciones motivo="Así te enteras de tus paquetes y pedidos aunque no tengas la app abierta." />
      <AvisoInstalar />
      <Boton variante="secundario" ocupado={ocupado === "salir"} onClick={() => ejecutar("salir", salir)}>
        Cerrar sesión
      </Boton>
      <p className="chico texto-suave centro">
        Tus datos se usan solo para prestar el servicio en tu conjunto (Ley 1581 de 2012). Para pedir que los borremos, escríbele a la
        administración.
      </p>
    </Pantalla>
  );
}

/** Cambiar la contraseña, sobre todo la que dio la administración al crear la cuenta. */
function CambiarClave() {
  const { cuenta } = useSesion();
  const { ejecutar, ocupado } = useAccion();
  const { avisar } = useAvisos();
  const [abierto, setAbierto] = useState(false);
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const corta = nueva.length > 0 && nueva.length < 8;

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (!cuenta?.email || nueva.length < 8) return;
    const r = await ejecutar("clave", async () => {
      try {
        await reauthenticateWithCredential(cuenta, EmailAuthProvider.credential(cuenta.email!, actual));
      } catch {
        throw new Error("La contraseña actual no es correcta.");
      }
      await updatePassword(cuenta, nueva);
      return true;
    });
    if (!r) return;
    avisar("Contraseña cambiada.");
    setAbierto(false);
    setActual("");
    setNueva("");
  };

  return (
    <Tarjeta>
      <h2>Contraseña</h2>
      {abierto ? (
        <form className="pila" onSubmit={guardar}>
          <Campo etiqueta="Contraseña actual" ayuda="La que usas para entrar, o la que te dio la administración.">
            <input type="password" autoComplete="current-password" required value={actual} onChange={(e) => setActual(e.target.value)} />
          </Campo>
          <Campo etiqueta="Contraseña nueva" error={corta ? "Mínimo 8 caracteres." : null}>
            <input type="password" autoComplete="new-password" required value={nueva} onChange={(e) => setNueva(e.target.value)} />
          </Campo>
          <div className="fila">
            <Boton type="button" variante="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={nueva.length < 8 || !actual} ocupado={ocupado === "clave"}>
              Guardar
            </Boton>
          </div>
        </form>
      ) : (
        <Boton variante="secundario" onClick={() => setAbierto(true)}>
          Cambiar contraseña
        </Boton>
      )}
    </Tarjeta>
  );
}
